import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';

import ScoreCard from '../../molecules/ScoreCard/ScoreCard';
import TimerDisplay from '../../molecules/TimerDisplay/TimerDisplay';
import ActionButton from '../../atoms/ActionButton/ActionButton';
import ClearButton from '../../atoms/ClearButton/ClearButton';
import { EventTimeline } from '@ae/event-timeline';
import CopilotClockBoard from './CopilotClockBoard';

import ClockOptionsDialog from './ClockOptionsDialog';
import ClockTimeInputDialog from './ClockTimeInputDialog';
import RemoveGoalConfirmDialog from './RemoveGoalConfirmDialog';
import { getPeriodEndSeconds, isMatchEnded } from '../../../hooks/useClockManager';
import ResetClockConfirmDialog from './ResetClockConfirmDialog';
import OverrideClockConfirmDialog from './OverrideClockConfirmDialog';
import { useServerClock } from '../../../hooks/useServerClock';
import timerOptions from './timerOptions.json';
import { resetFixturePreset } from '@aelive/dds-client';
import { getStoredSession } from '../../../utils/sessionManager';
import { sendPatchesFromDrivePreset } from '@aelive/dds-client';
import { dds } from '../../../dds/ddsClient';
import { computeRedCardTabs, computeOpusGoalScores, createPlayerLookup, buildScorersByTeam, findLatestTeamGoal } from '../../../utils/txLiveHelpers';
import { upsertTeamStat } from '../../../utils/liveEventUtils';
import { getTeamFirstName, getEventName, playerKeys } from '../../../utils/playerName';

import { StatsContainer, StatsPanel } from '@ae/ui';
import CopilotDoubleStatsGrid from './CopilotDoubleStatsGrid';
import CopilotSingleStatsGrid from './CopilotSingleStatsGrid';
import CopilotAdvancedStatsGrid from './CopilotAdvancedStatsGrid';

/* ------------------------------------------------------------------ */
/* TYPES + HELPERS */
/* ------------------------------------------------------------------ */

export const mapStatsToPanelItems = (stats) =>
  stats.map((row) => ({
    key: row.key ?? row.id ?? row.label,
    label: row.label,
    leftValue: row.home,
    rightValue: row.away,
    ...(row.displayType && { displayType: row.displayType }),
  }));

// Single caption tiles ({ name, title, home, away }) → shared-grid items.
// `name` (the caption) rides along so onStatClick can open that caption.
const mapSingleTilesToItems = (tiles) =>
  tiles.map((t) => ({ key: t.name, name: t.name, label: t.title, leftValue: t.home, rightValue: t.away }));

/* ------------------------------------------------------------------ */
/* DEFAULT DATA */
/* ------------------------------------------------------------------ */

const defaultScore = {
  home: { team: '', score: 0, status: 'GOAL' },
  away: { team: '', score: 0, status: 'GOAL' },
  period: 'PRE-GAME',
  timer: '00:00',
  competition: '',
};

const defaultActions = [
  { id: 'time', label: 'Time Control' },
  { id: 'scorer', label: 'Change Scorer' },
  { id: 'undo', label: 'Undo' },
  { id: 'log', label: 'Log Events' },
];

const defaultStats = [
  { id: 'shots', label: 'Shots', home: '00', away: '00' },
  { id: 'shotsOnTarget', label: 'Shots on Target', home: '00', away: '00' },
  { id: 'shotsOffTarget', label: 'Shots off Target', home: '00', away: '00' },
  { id: 'fouls', label: 'Fouls Committed', home: '00', away: '00' },
  { id: 'corners', label: 'Corners', home: '00', away: '00' },
  { id: 'offsides', label: 'Offsides', home: '00', away: '00' },
  { id: 'tackles', label: 'Tackles', home: '00', away: '00' },
  { id: 'passesCompleted', label: 'Passes Completed', home: '00', away: '00' },
  { id: 'passesAttempted', label: 'Passes Attempted', home: '00', away: '00' },
  { id: 'passCompletion', label: 'Pass completion', home: '00', away: '00' },
  { id: 'possession', label: 'Possession', home: '00', away: '00' },
];

const defaultOtherGames = [
  { id: 'game-1', home: { team: 'ABC', score: '0' }, away: { team: 'XYZ', score: '0' } },
  { id: 'game-2', home: { team: 'QPR', score: '0' }, away: { team: 'FUL', score: '0' } },
];

/* ------------------------------------------------------------------ */
/* COMPONENT */
/* ------------------------------------------------------------------ */

const StatsPanels = React.memo(({
  score = defaultScore,
  stats = defaultStats,
  otherGames = defaultOtherGames,
  homeAccent = '#45FFD5',
  awayAccent = '#D1030D',
  onTimerAction,
  onTimeControlClick,
  onClockOptionSelect,
  rendererId = 'OP-BLUEBIRD-TEST929',
  clockName = 'MatchClock',
  statsViewMode = 'stats',
  onStatsViewModeChange,
  eventLog = [],
  hideStats = false,
  hideClock = false,
  isCopilot = false,
  copilotScale = 1,
  onScoreChange,
  onAddGoalClick,
  // Copilot score-tile click → edit that team's latest goal (the "−" button
  // removes it, after the confirm below).
  onEditGoalClick,
  // Copilot EVENTS modal (opened from the tabs header in TXLive)
  eventsModalOpen = false,
  onCloseEventsModal,
  // When set, the events timeline auto-opens this goal's overwrite dialog
  // (used by "Reassign Goalscorer"). onAutoEditHandled clears it afterwards.
  autoEditEventId = null,
  onAutoEditHandled,
  // Copilot caption-sourced STATS tiles (MatchStats_* / MatchStats_x2_*).
  // When provided, the copilot grid renders Single/Double tabs from these
  // instead of the in-app teamStats.
  singleStatTiles = null,
  doubleStatTiles = null,
  advancedStatTiles = null,
  // Caption currently animated IN — its tile gets the ON-AIR highlight.
  activeStatCaption = null,
  // Real on-air flag from the broker — makes the Clear button show "live" even
  // when the specific tile can't be matched.
  statsGraphicOnAir = false,
  // Clears the on-air stats graphic (animate OUT). Shown as a button atop the grid.
  onClearStatsGraphic,
  // Copilot stat-button click → open the maestro stats graphic
  onStatClick,
  // Operator substitution workflow props
  fixtureId,
  competitionId,
  seasonId,
  homeTeam,
  awayTeam,
  onOpenSubstitutionsDialog,
  // Operator goal workflow props
  fixture,
  // Competition name for the Ident strap line; lives on fixtureState, not
  // on fixture, so TXLive passes it down the same way it does profileId.
  competitionName,
  // Rides on the match-reset event this panel publishes itself; every other
  // event gets it from useEventService.
  fixtureName,
  result,
  profileId,
  userRole,
  eventService,
  triggerScoreUpdate,
  updateFixtureState,
  homeLineup,
  awayLineup,
  fixtureEvents,
  // Clock workflow props
  clockProfile,
  clockTime,
  period,
  setPeriod,
  triggerClockAction,
  getRendererIdToUse,
  publishGoalEvent,
  sendApplyDataState,
  cancelPendingScoreFinalise,
  updateRenderer,
  onUpdateEvent,
  onNotify,
}) => {
  const [timerModalOpen, setTimerModalOpen] = useState(false);
  // Copilot STATS sub-tab: 'single' (MatchStats_*), 'double' (MatchStats_x2_*) or 'advanced'.
  const [statTab, setStatTab] = useState('single');
  const statsLive = Boolean(activeStatCaption || statsGraphicOnAir);

  // Spans both squads — buildScorersByTeam needs it to name own-goal scorers,
  // who belong to the team that did NOT get the goal.
  const playerLookup = useMemo(
    () => createPlayerLookup(homeTeam, awayTeam),
    [homeTeam, awayTeam],
  );

  const handleEditEvent = useCallback(async ({ event_id, changedData, sourceEvent }) => {
    try {
      await onUpdateEvent?.({ event_id, fixture_id: fixtureId, competition_id: competitionId, season_id: seasonId, changedData, sourceEvent });
      onNotify?.('Event updated successfully', 'success');
    } catch (err) {
      console.error('[StatsPanel] event update failed', err);
      onNotify?.(`Failed to update event: ${err?.message || 'Unknown error'}`, 'error');
    }
  }, [onUpdateEvent, onNotify, fixtureId, competitionId, seasonId]);

  const handleRemoveEvent = useCallback(async (id) => {
    const match = fixtureEvents?.find((e) => e.event_id === id);
    if (!match) return;

    const cardKeys = new Set(['yellow_card', 'red_card', 'double_yellow']);
    const goalKeys = new Set(['goal', 'own_goal', 'penalty']);
    const subKeys = new Set(['sub', 'concussion_sub']);
    const isGoal = goalKeys.has(match.event_key);
    const isCard = cardKeys.has(match.event_key);
    const isSub = subKeys.has(match.event_key);
    if (!isGoal && !isCard && !isSub) return;

    updateFixtureState?.({ collection: 'event', op: 'delete', key: { fixture_id: fixtureId, event_id: id }, data: { event_id: id } });

    try {
      const eventsAfter = (fixtureEvents || []).filter((e) => e.event_id !== id);
      const isHome = match.team_id === homeTeam?.team_id;
      const newScore = computeOpusGoalScores(eventsAfter, homeTeam?.team_id, awayTeam?.team_id);
      const redCardTabs = computeRedCardTabs(eventsAfter, homeTeam?.team_id, awayTeam?.team_id);

      if (isGoal) {
        const triggerResult = await eventService?.trigger({
          type: 'goal', event_op: 'delete', event_key: match.event_key, goal_type: match.event_key,
          event_id: id, delete_reason: 'manual', fixtureId, competitionId, seasonId,
          team_id: match.team_id, player1_id: match.player1_id, time_in_seconds: match.time_in_seconds,
          ...playerKeys(playerLookup.get(match.player1_id)),
          game_period: match.game_period ?? fixture?.fixture_session ?? null,
          team_side: isHome ? 'home' : 'away', new_score: newScore,
          old_score: { home_team_score: result?.home_team_score || 0, away_team_score: result?.away_team_score || 0 },
          homeTeam, awayTeam, redCardTabs, rendererId: getRendererIdToUse ? getRendererIdToUse() : rendererId,
          profile_id: profileId, user_role: userRole,
          optimisticState: {
            collection: 'fixture_result',
          // OPUS-1505 — the key is REQUIRED, not decoration. applyStaticDataPatch's
          // patchSeasonFixtureResult and patchSeasonFixture both do
          // `if (!fixtureId) return` before touching season[].fixture[], so an
          // optimistic patch without one updates the flat root.fixture_result[0]
          // and leaves the NESTED season.fixture[].fixture_result untouched. The
          // header score moved while season.fixture[] still read null until the
          // server round-trip landed. The sibling `event` optimistic patch has
          // always carried a key; these did not.
            key: { fixture_id: fixtureId },
            data: { home_team_score: newScore.home_team_score, away_team_score: newScore.away_team_score },
          },
        });
        for (const cmd of triggerResult?.rendererCommands ?? []) {
          try { await updateRenderer?.(cmd); } catch (e) { console.error('Failed renderer cmd on goal delete', e); }
        }
      } else if (isCard) {
        const scores = computeOpusGoalScores(eventsAfter, homeTeam?.team_id, awayTeam?.team_id);
        const triggerResult = await eventService?.trigger({
          type: 'card', event_op: 'delete', event_key: match.event_key, card_type: match.event_key,
          event_id: id, delete_reason: 'manual', fixtureId, competitionId, seasonId,
          team_id: match.team_id, player1_id: match.player1_id, time_in_seconds: match.time_in_seconds,
          ...playerKeys(playerLookup.get(match.player1_id)),
          game_period: match.game_period ?? fixture?.fixture_session ?? null,
          team_side: isHome ? 'home' : 'away', homeTeam, awayTeam, scores, redCardTabs,
          fixtureEvents: fixtureEvents || [], rendererId: getRendererIdToUse ? getRendererIdToUse() : rendererId,
          profile_id: profileId, user_role: userRole,
        });
        for (const cmd of triggerResult?.rendererCommands ?? []) {
          try { await updateRenderer?.(cmd); } catch (e) { console.error('Failed renderer cmd on card delete', e); }
        }
      } else if (isSub) {
        const triggerResult = await eventService?.trigger({
          type: 'sub', event_op: 'delete', event_key: match.event_key, sub_type: match.event_key,
          event_id: id, delete_reason: 'manual', fixtureId, competitionId, seasonId,
          team_id: match.team_id, player1_id: match.player1_id, player2_id: match.player2_id,
          // player1 went off, player2 came on.
          ...playerKeys(playerLookup.get(match.player1_id), 'Off'),
          ...playerKeys(playerLookup.get(match.player2_id), 'On'),
          time_in_seconds: match.time_in_seconds,
          game_period: match.game_period ?? fixture?.fixture_session ?? null,
          team_side: isHome ? 'home' : 'away',
          rendererId: getRendererIdToUse ? getRendererIdToUse() : rendererId,
          profile_id: profileId, user_role: userRole,
        });
        for (const cmd of triggerResult?.rendererCommands ?? []) {
          try { await updateRenderer?.(cmd); } catch (e) { console.error('Failed renderer cmd on sub delete', e); }
        }
      }
    } catch (e) {
      console.error('Event removal trigger failed', e);
    }
  }, [fixtureEvents, fixtureId, competitionId, seasonId, homeTeam, awayTeam, fixture, result, profileId, userRole, eventService, updateFixtureState, updateRenderer, rendererId, getRendererIdToUse, playerLookup]);

  // Copilot "−": confirm, then remove that team's latest saved goal (OPUS-1635).
  // null = closed; else 'home' | 'away'.
  const [removeGoalSide, setRemoveGoalSide] = useState(null);

  const handleRemoveGoalClick = useCallback((teamSide) => {
    // Rule: once the match is finished ("FULL TIME") the score is locked.
    if (period === 'FULL TIME' || isMatchEnded(fixtureEvents)) {
      onNotify?.("Match ended — goals can't be removed after full time.", 'warning');
      return;
    }
    setRemoveGoalSide(teamSide);
  }, [period, fixtureEvents, onNotify]);

  const handleConfirmRemoveGoal = useCallback(async () => {
    const teamSide = removeGoalSide;
    setRemoveGoalSide(null);
    const teamId = teamSide === 'home' ? homeTeam?.team_id : awayTeam?.team_id;
    const latestGoal = findLatestTeamGoal(fixtureEvents, teamId);
    if (!latestGoal?.event_id) {
      onNotify?.('No saved goal found for this team.', 'warning');
      return;
    }
    await handleRemoveEvent(latestGoal.event_id);
    onNotify?.('Goal removed', 'success');
  }, [removeGoalSide, homeTeam, awayTeam, fixtureEvents, handleRemoveEvent, onNotify]);

  const toPlayerInfo = (p) => ({
    id: p.lineup_id || p.player_id || p.id || '',
    name: (() => {
      const n = p.unique_name || p.player_name || [p.player_first_name, p.player_last_name].filter(Boolean).join(' ') || p.player_id || '';
      return p.shirt_number ? `${p.shirt_number} - ${n}` : n;
    })(),
  });

  const mappedHomeLineup = useMemo(() => ({
    players: (homeLineup?.players || []).map(toPlayerInfo),
    subs: (homeLineup?.subs || []).map(toPlayerInfo),
  }), [homeLineup]);

  const mappedAwayLineup = useMemo(() => ({
    players: (awayLineup?.players || []).map(toPlayerInfo),
    subs: (awayLineup?.subs || []).map(toPlayerInfo),
  }), [awayLineup]);

  const homeTeamInfo = useMemo(() => ({
    id: homeTeam?.team_id,
    name: homeTeam?.team_short_name || homeTeam?.team_name || 'Home',
  }), [homeTeam]);

  const awayTeamInfo = useMemo(() => ({
    id: awayTeam?.team_id,
    name: awayTeam?.team_short_name || awayTeam?.team_name || 'Away',
  }), [awayTeam]);

  // Clock workflow state
  const [clockOptionsDialogOpen, setClockOptionsDialogOpen] = useState(false);
  const [clockTimeInputDialogOpen, setClockTimeInputDialogOpen] = useState(false);
  const [resetClockConfirmDialogOpen, setResetClockConfirmDialogOpen] = useState(false);
  // Clock-override confirm (see TXLive) — null = closed; else pending start params.
  const [clockOverride, setClockOverride] = useState(null);
  const getServerNow = useServerClock();
  const [clockConstraints, setClockConstraints] = useState({});

  // "Log Substitutions" opens the same shared SubstitutionDialog (@ae/ui) that
  // copilot uses — see CopilotSubstitutionDialog, rendered once in TXLive.jsx
  // regardless of mode.
  const handleClockSubsClick = useCallback(() => {
    onOpenSubstitutionsDialog?.();
  }, [onOpenSubstitutionsDialog]);

  // Operator goal workflow state
  const [goalLogDialogOpen, setGoalLogDialogOpen] = useState(false);
  const [playerSelectionDialogOpen, setPlayerSelectionDialogOpen] = useState(false);
  const [goalTimeDialogOpen, setGoalTimeDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedGoalType, setSelectedGoalType] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const selectedTeamRef = useRef(null);
  const selectedGoalTypeRef = useRef(null);
  const selectedPlayerRef = useRef(null);
  const selectedGoalScoreRef = useRef(null);
  const oldScoreBeforeGoalRef = useRef(null);
  const lastScoreGraphicCommandsRef = useRef(null);
  const goalFlowProgressingRef = useRef(false);
  // Snapshot of the match clock at the first step of the goal-logging flow.
  // Reused as the pre-fill for the time-confirmation dialog so the logged
  // time matches when the operator actually pressed the button — not when
  // they finished selecting a player (which can be 10–30s later).
  const goalFlowStartClockRef = useRef(null);
  // Mirrors `result`, but updated synchronously on each optimistic bump/revert — avoids basing the next +1 on a stale `result` when goals are logged back-to-back.
  const latestKnownScoreRef = useRef({
    home_team_score: result?.home_team_score ?? 0,
    away_team_score: result?.away_team_score ?? 0,
  });

  useEffect(() => {
    latestKnownScoreRef.current = {
      home_team_score: result?.home_team_score ?? 0,
      away_team_score: result?.away_team_score ?? 0,
    };
  }, [result]);

  const handleGoalTypeSelect = useCallback(
    async (type) => {
      const currentTeam = selectedTeamRef.current;
      if (!currentTeam && !selectedTeam) return;

      goalFlowProgressingRef.current = true;
      setSelectedGoalType(type);
      selectedGoalTypeRef.current = type;
      setGoalLogDialogOpen(false);
      setPlayerSelectionDialogOpen(true);
    },
    [selectedTeam],
  );

  const handlePlayerSelect = useCallback((player) => {
    goalFlowProgressingRef.current = true;
    setSelectedPlayer(player);
    selectedPlayerRef.current = player;
    setPlayerSelectionDialogOpen(false);
    setGoalTimeDialogOpen(true);
  }, []);

  const cancelGoalLogging = useCallback(() => {
    if (goalFlowProgressingRef.current) {
      goalFlowProgressingRef.current = false;
      return;
    }

    setGoalLogDialogOpen(false);
    setPlayerSelectionDialogOpen(false);
    setGoalTimeDialogOpen(false);

    const oldScore = oldScoreBeforeGoalRef.current;
    const team = selectedTeamRef.current || selectedTeam;

    // If the operator cancels AFTER picking a goal type, triggerScoreUpdate
    // already bumped the renderer's PermScore optimistically. Send an
    // APPLY_DATA frame that snaps it back to the pre-bump scores so the
    // graphic isn't out of sync with the actual match state.
    if (oldScore && team && sendApplyDataState) {
      // Scoped to `team` — an unscoped call would also drop the other team's still-pending finalise.
      cancelPendingScoreFinalise?.(team);
      latestKnownScoreRef.current = oldScore;
      const reds = computeRedCardTabs(
        fixtureEvents || [],
        homeTeam?.team_id,
        awayTeam?.team_id,
      );
      sendApplyDataState({
        scores: {
          home: oldScore.home_team_score ?? 0,
          away: oldScore.away_team_score ?? 0,
        },
        homeTeam,
        awayTeam,
        // Preserve existing red-card state — cancel is reverting the goal
        // bump only, not touching reds.
        redCardTabs: reds,
      }).catch((e) => {
        console.error("[StatsPanel] cancel-goal APPLY_DATA revert failed", e);
      });
    }

    setSelectedTeam(null);
    selectedTeamRef.current = null;
    setSelectedGoalType(null);
    selectedGoalTypeRef.current = null;
    setSelectedPlayer(null);
    selectedPlayerRef.current = null;
    selectedGoalScoreRef.current = null;
    oldScoreBeforeGoalRef.current = null;
    goalFlowStartClockRef.current = null;
  }, [selectedTeam, sendApplyDataState, cancelPendingScoreFinalise, fixtureEvents, homeTeam, awayTeam]);

  const saveGoalEvent = useCallback(
    async (timeInSeconds) => {
      const currentTeam = selectedTeam || selectedTeamRef.current;
      const currentPlayer = selectedPlayer || selectedPlayerRef.current;
      const currentGoalType = selectedGoalType || selectedGoalTypeRef.current;

      if (!currentPlayer || !currentGoalType || !currentTeam || !fixture) return;

      const isHome = currentTeam === "home";
      const teamId = isHome ? homeTeam?.team_id : awayTeam?.team_id;

      // goal_event_object_builder.js (@bb/event-service) IGNORES team_id for
      // own_goal — it derives the credited team purely from team_side
      // (flipping it internally) plus the homeTeam/awayTeam objects. Since it
      // flips team_side, send the OPPOSITE side for own_goal so its flip
      // lands back on the actually-clicked (correctly credited) side.
      const isOwnGoal = currentGoalType === "own_goal";
      const teamSide = isOwnGoal ? (isHome ? "away" : "home") : currentTeam;

      const newScore = selectedGoalScoreRef.current ?? {
        home_team_score: result?.home_team_score ?? 0,
        away_team_score: result?.away_team_score ?? 0,
      };
      const oldScore = oldScoreBeforeGoalRef.current ?? {
        home_team_score: result?.home_team_score ?? 0,
        away_team_score: result?.away_team_score ?? 0,
      };

      // Not cancelling the pending score-finalise here — that 4s timer is the only thing that sends the final "Score" BAKE on a create.
      oldScoreBeforeGoalRef.current = null;
      goalFlowStartClockRef.current = null;

      setGoalTimeDialogOpen(false);

      setTimeout(() => {
        setSelectedTeam(null);
        selectedTeamRef.current = null;
        selectedGoalScoreRef.current = null;
      }, 300);

      console.log('[StatsPanel] ▶ user confirmed goal-time', { goal_type: currentGoalType, team_side: currentTeam, player_id: currentPlayer.id, timeInSeconds, newScore });
      try {
        const eventResult = await eventService?.trigger({
          type: "goal",
          event_op: 'create',
          goal_type: currentGoalType,
          fixtureId,
          competitionId,
          seasonId,
          team_id: teamId,
          player1_id: currentPlayer.id,
          shirt_number: currentPlayer.shirt_number ?? null,
          time_in_seconds: timeInSeconds,
          game_period: fixture?.fixture_session ?? null,
          team_side: teamSide,
          new_score: newScore,
          old_score: oldScore,
          homeTeam,
          awayTeam,
          profile_id: profileId,
          user_role: userRole,
          graphics: lastScoreGraphicCommandsRef.current,
          optimisticState: {
            collection: "fixture_result",
          // OPUS-1505 — the key is REQUIRED, not decoration. applyStaticDataPatch's
          // patchSeasonFixtureResult and patchSeasonFixture both do
          // `if (!fixtureId) return` before touching season[].fixture[], so an
          // optimistic patch without one updates the flat root.fixture_result[0]
          // and leaves the NESTED season.fixture[].fixture_result untouched. The
          // header score moved while season.fixture[] still read null until the
          // server round-trip landed. The sibling `event` optimistic patch has
          // always carried a key; these did not.
            key: { fixture_id: fixtureId },
            data: {
              home_team_score: newScore.home_team_score,
              away_team_score: newScore.away_team_score,
            },
          },
        });
        console.log('[StatsPanel] ✔ eventService.trigger returned', eventResult);
      } catch (e) {
        console.error('[StatsPanel] ✗ failed to persist goal', e);
      }
    },
    [
      selectedTeam,
      selectedPlayer,
      selectedGoalType,
      fixture,
      homeTeam,
      awayTeam,
      result,
      fixtureId,
      eventService,
      competitionId,
      seasonId,
      updateFixtureState,
      profileId,
      userRole,
    ],
  );

  // Expose goal flow entry point for score area clicks
  const handleScoreAreaGoalClick = useCallback((teamSide) => {
    // Ignore a repeat click while a goal flow is already in progress.
    if (selectedTeamRef.current) return;

    setSelectedTeam(teamSide);
    selectedTeamRef.current = teamSide;
    // Capture the match clock as-of this click. GoalTimeDialog opens 2+
    // steps later (goal type + player) and would otherwise pre-fill with a
    // stale "now" that has drifted from when the goal actually happened.
    goalFlowStartClockRef.current = clockTime;

    const clickedIsHome = teamSide === "home";
    const baseScore = latestKnownScoreRef.current;
    const newScore = {
      home_team_score: baseScore.home_team_score + (clickedIsHome ? 1 : 0),
      away_team_score: baseScore.away_team_score + (clickedIsHome ? 0 : 1),
    };
    oldScoreBeforeGoalRef.current = baseScore;
    selectedGoalScoreRef.current = newScore;
    latestKnownScoreRef.current = newScore;
    // (triggerScoreUpdate self-clears/reschedules this side's pending finalise internally)

    triggerScoreUpdate?.(teamSide, newScore, homeTeam, awayTeam).then((commands) => {
      lastScoreGraphicCommandsRef.current = commands;
    });

    setGoalLogDialogOpen(true);
  }, [clockTime, homeTeam, awayTeam, triggerScoreUpdate]);

  const nextClockOptionId = clockProfile?.periodToNextOption?.[period];

  const handleTimeControlClick = useCallback(() => {
    setClockOptionsDialogOpen(true);
  }, []);

  // Clock event triggers — the matching clock_*_event_object_builder in
  // BB_EventService consumes (fixture_session, clock_status, opta_match_status)
  // to derive the new fixture_status via the matrix and emits the DDS patches
  // for all three fields. Optimistic state mirrors what the builder will
  // persist so the local timeline reflects the change immediately.
  const triggerClockEvent = useCallback(async (eventKey, clockState = {}) => {
    if (!eventService) return;
    const session = getStoredSession();
    const parseSeconds = (str) => {
      if (!str) return 0;
      const [m, s] = String(str).split(':').map(Number);
      if (Number.isNaN(m)) return 0;
      return m * 60 + (Number.isNaN(s) ? 0 : s);
    };
    const fixtureSession = clockState.fixtureSession ?? null;
    const clockStatus = clockState.clockStatus ?? 0;
    try {
      // #8: a re-trigger must OVERRIDE the existing marker, not append a duplicate.
      // DDS upserts events by event_id and eventService.trigger stamps a fresh id
      // each call, so a clock re-start / re-entered added-time created a SECOND
      // event (two KICK-OFF lines, ADDITIONAL TIME 4'+5'). Delete any existing
      // event(s) with this event_key (+ matching period) first, so the timeline
      // keeps exactly one. The on-air clock is broker-driven (triggerClockAction),
      // not this marker, so clearing the marker doesn't disturb the running clock.
      // ⚠️ LIVE on-air path — verify on a running clock before trusting.
      const dupes = (fixtureEvents || []).filter(
        (e) =>
          (e.event_key || e.event_type) === eventKey &&
          (fixtureSession == null || (e.game_period ?? e.fixture_session) === fixtureSession),
      );
      for (const ev of dupes) {
        if (!ev.event_id) continue;
        try {
          await eventService.trigger({
            type: eventKey,
            event_op: 'delete',
            event_key: eventKey,
            event_id: ev.event_id,
            delete_reason: 'clock_override',
            renderer: getRendererIdToUse?.(),
            fixtureId,
            competitionId,
            seasonId,
            fixture_id: fixtureId,
            competition_id: competitionId,
            season_id: seasonId,
            game_period: fixtureSession,
          });
        } catch (delErr) {
          console.error(`Failed to clear duplicate ${eventKey} before re-trigger`, delErr);
        }
      }
      await eventService.trigger({
        type: eventKey,
        event_op: 'create',
        renderer: getRendererIdToUse?.(),
        fixtureId,
        competitionId,
        seasonId,
        fixture_id: fixtureId,
        competition_id: competitionId,
        season_id: seasonId,
        game_period: fixtureSession,
        fixture_session: fixtureSession,
        clock_status: clockStatus,
        // Clock override: the operator's override path runs through StatsPanel, so
        // it must forward the captured server-anchored click instant — DDS
        // preserves a supplied ref_clock so the clock restarts from the click, not
        // the override-confirm. A normal start omits it and DDS stamps.
        ...(clockState.refClock ? { ref_clock: clockState.refClock } : {}),
        opta_match_status: fixture?.opta_match_status ?? null,
        homeTeam,
        awayTeam,
        home_team_score: result?.home_team_score ?? 0,
        away_team_score: result?.away_team_score ?? 0,
        // Ident strap lines (gfx_LF_Ident strTitle / strVenue). Only
        // clock_start_1h / clock_start_2h read these.
        venue_name: fixture?.venue?.[0]?.venue_name ?? '',
        competition_name: competitionName ?? '',
        // Scorers for the half/full-time scoreline. Only clock_end_1h /
        // clock_end_2h read these; the other clock_* builders ignore them.
        homeScorers: buildScorersByTeam(fixtureEvents, playerLookup, homeTeam?.team_id),
        awayScorers: buildScorersByTeam(fixtureEvents, playerLookup, awayTeam?.team_id),
        optimisticState: {
          collection: 'fixture',
          // OPUS-1505 — the key is REQUIRED, not decoration. applyStaticDataPatch's
          // patchSeasonFixtureResult and patchSeasonFixture both do
          // `if (!fixtureId) return` before touching season[].fixture[], so an
          // optimistic patch without one updates the flat root.fixture_result[0]
          // and leaves the NESTED season.fixture[].fixture_result untouched. The
          // header score moved while season.fixture[] still read null until the
          // server round-trip landed. The sibling `event` optimistic patch has
          // always carried a key; these did not.
          key: { fixture_id: fixtureId },
          data: { fixture_session: fixtureSession, clock_status: clockStatus },
        },
        // Overwrite Clock passes the operator-typed time explicitly — clockTime
        // (component state) may not have caught up with what was just typed yet.
        time_in_seconds: clockState.timeInSeconds ?? parseSeconds(clockTime),
        triggered_by: session?.username || session?.user_name || null,
        profile_id: profileId,
        user_role: userRole,
      });
    } catch (e) {
      console.error(`Failed to trigger ${eventKey} event`, e);
    }
  }, [eventService, getRendererIdToUse, fixtureId, competitionId, seasonId, clockTime, profileId, userRole, fixture, competitionName, fixtureEvents, homeTeam, awayTeam, result, playerLookup]);

  const handleClockOptionSelect = useCallback(async (option) => {
    setClockOptionsDialogOpen(false);

    if (option.id === 'overwrite') {
      let min = undefined;
      let max = undefined;
      if (period === 'FIRST HALF') {
        min = 0;
        max = Math.ceil(clockProfile.firstHalfLimit / 60);
      } else if (period === 'SECOND HALF') {
        min = 45;
        max = Math.ceil(clockProfile.secondHalfLimit / 60);
      }
      setClockConstraints({ min, max });
      setClockTimeInputDialogOpen(true);
      return;
    }

    if (option.id === 'reset') {
      // Open the confirmation dialog only. The actual reset (period change,
      // clock_match_reset event, broker stop, score zero) runs from the
      // dialog's onConfirm so nothing persists until the operator types
      // "reset" and clicks the button.
      setResetClockConfirmDialogOpen(true);
      return;
    }

    if (option.id === 'startFirstHalf') {
      // Override guard (see OverrideClockConfirmDialog): a clock_start_1h already
      // exists → confirm before restarting; capture the server-anchored click
      // instant so the restart runs from the click, not the confirm.
      const exists1 = (fixtureEvents || []).some((e) => (e.event_key || e.event_type) === 'clock_start_1h');
      if (exists1) {
        setClockOverride({ eventKey: 'clock_start_1h', fixtureSession: 1, period: 'FIRST HALF', periodLabel: 'First Half', brokerAction: 'START', brokerTime: '00:00', refClock: new Date(getServerNow()).toISOString() });
        return;
      }
      onClockOptionSelect?.(option);
      if (setPeriod) setPeriod('FIRST HALF');
      await triggerClockAction('START', '00:00', 'UP');
      await triggerClockEvent('clock_start_1h', { fixtureSession: 1, clockStatus: 1 });
    }
    if (option.id === 'endFirstHalf') {
      onClockOptionSelect?.(option);
      if (setPeriod) setPeriod('HALF TIME');
      await triggerClockAction('STOP_1ST_HALF');
      const endSeconds = getPeriodEndSeconds(fixtureEvents, 1, clockProfile.firstHalfEndSeconds, clockProfile.secondHalfEndSeconds);
      await triggerClockEvent('clock_end_1h', { fixtureSession: 1, clockStatus: 0, timeInSeconds: endSeconds });
    }
    if (option.id === 'startSecondHalf') {
      const exists2 = (fixtureEvents || []).some((e) => (e.event_key || e.event_type) === 'clock_start_2h');
      if (exists2) {
        setClockOverride({ eventKey: 'clock_start_2h', fixtureSession: 2, period: 'SECOND HALF', periodLabel: 'Second Half', brokerAction: 'START_2ND_HALF', brokerTime: '45:00', refClock: new Date(getServerNow()).toISOString() });
        return;
      }
      onClockOptionSelect?.(option);
      if (setPeriod) setPeriod('SECOND HALF');
      await triggerClockAction('START_2ND_HALF', '45:00', 'UP');
      await triggerClockEvent('clock_start_2h', { fixtureSession: 2, clockStatus: 1 });
    }
    if (option.id === 'endSecondHalf') {
      onClockOptionSelect?.(option);
      if (setPeriod) setPeriod('FULL TIME');
      await triggerClockAction('STOP_2ND_HALF');
       const endSeconds = getPeriodEndSeconds(fixtureEvents, 2, clockProfile.firstHalfEndSeconds, clockProfile.secondHalfEndSeconds);
      await triggerClockEvent('clock_end_2h', { fixtureSession: 2, clockStatus: 0, timeInSeconds: endSeconds });
    }
  }, [period, clockProfile, setPeriod, triggerClockAction, triggerClockEvent, fixtureEvents, getServerNow, onClockOptionSelect]);

  const doClockOverride = useCallback(async () => {
    const o = clockOverride;
    if (!o) return;
    setClockOverride(null);
    onClockOptionSelect?.({ id: o.eventKey === 'clock_start_2h' ? 'startSecondHalf' : 'startFirstHalf', ...o });
    if (setPeriod) setPeriod(o.period);
    await triggerClockAction(o.brokerAction, o.brokerTime, 'UP');
    await triggerClockEvent(o.eventKey, { fixtureSession: o.fixtureSession, clockStatus: 1, refClock: o.refClock });
  }, [clockOverride, setPeriod, triggerClockAction, triggerClockEvent, onClockOptionSelect]);

  const handleClockTimeConfirm = useCallback(async (timeString, totalSeconds) => {
    setClockTimeInputDialogOpen(false);
    const minutes = totalSeconds / 60;
    let nextSession;
    if (minutes <= 45) {
      if (setPeriod) setPeriod('FIRST HALF');
      nextSession = 1;
    } else {
      if (setPeriod) setPeriod('SECOND HALF');
      nextSession = 2;
    }
    onClockOptionSelect?.({ id: minutes <= 45 ? 'startFirstHalf' : 'startSecondHalf' });
    await triggerClockAction('START', timeString);
    // Dedicated clock_overwrite event (not clock_start_1h/2h) — routes through
    // triggerClockEvent so clock_status/fixture_status get derived and the
    // timeline marker gets written, same as every other clock action, while
    // correctly labeling the timeline entry "Overwrite Clock" instead of a
    // misleading "Start First/Second Half".
    await triggerClockEvent('clock_overwrite', {
      fixtureSession: nextSession,
      clockStatus: 1,
      timeInSeconds: totalSeconds,
      refClock: new Date(getServerNow()).toISOString(),
    });
  }, [setPeriod, triggerClockAction, triggerClockEvent, getServerNow, onClockOptionSelect]);

  const actions = useMemo(() => {
    if (isCopilot) return [];
    return [
      {
        id: "clockSubs",
        label: "Log Substitutions",
        onClick: handleClockSubsClick,
      },
    ];
  }, [isCopilot, handleClockSubsClick]);

  // The live match clock is driven by useClockManager (VizGlobal.Clocks over
  // the command subscription) and arrives here as score.timer. The old
  // useClock hook queried getClockData, a broker Query field that does not
  // exist (GraphQL "Cannot query field getClockData"), and never produced
  // data — hasData was always false — so displayTimer is just score.timer.
  const displayTimer = score.timer;

  const showEvents = statsViewMode === 'events';

  const handleTabChange = (_, newValue) => {
    onStatsViewModeChange?.(newValue === 0 ? 'stats' : 'events');
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        color: '#FFFFFF',
        fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
      }}
    >
      {/* SCORE + TIMER */}
      {!hideClock && (
        isCopilot ? (
          <CopilotClockBoard
            score={score}
            homeAccent={homeAccent}
            awayAccent={awayAccent}
            onScoreChange={onScoreChange}
            onAddGoalClick={onAddGoalClick}
            onRemoveGoalClick={handleRemoveGoalClick}
            onEditGoalClick={onEditGoalClick}
            displayTimer={displayTimer}
            scale={copilotScale}
            // Clicking the clock opens the operator ClockOptionsDialog.
            onTimerClick={() => handleTimeControlClick()}
          />
        ) : (
          <Stack
            direction="row"
            spacing={0}
            justifyContent="center"
            alignItems="center"
            sx={{ position: 'relative' }}
          >
             <ScoreCard {...score.home} highlight accent={homeAccent} onClick={() => onAddGoalClick?.('home')} />
            <Box sx={{ position: 'relative', zIndex: 2, mx: -0.5 }}>
              <TimerDisplay
                period={score.period}
                timer={displayTimer}
                competition={score.competition}
                accentColor={homeAccent}
                onClick={() => {
                  handleTimeControlClick();
                }}
              />
            </Box>
           <ScoreCard {...score.away} highlight accent={awayAccent} onClick={() => onAddGoalClick?.('away')} />
          </Stack>
        )
      )}

      {/* ACTIONS */}
      {!hideClock && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: actions.length === 1 ? '1fr' : 'repeat(2, 1fr)',
            gap: 1,
            width: '100%',
          }}
        >
          {actions.map((action) => (
            <ActionButton
              key={action.id}
              label={action.label}
              onClick={
                action.id === 'time'
                  ? handleTimeControlClick
                  : action.onClick
              }
            />
          ))}
        </Box>
      )}

      {/* Panel: Stats/Events Tabs */}
      {!hideStats && (
        <>
          {/* Copilot reaches events via the header EVENTS button + modal, so the
              inline Stats/Events toggle is hidden in that view. */}
          {onStatsViewModeChange && !isCopilot && (
            <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'rgba(255,255,255,0.1)' }}>
              <Tabs
                value={statsViewMode === 'stats' ? 0 : 1}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  minHeight: 40,
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#FFFFFF',
                    height: 3,
                  },
                  '& .MuiTab-root': {
                    minHeight: 40,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: 14,
                    color: 'rgba(255,255,255,0.5)',
                    fontFamily: '"Barlow", "Roboto", "Helvetica", Arial, sans-serif',
                    '&.Mui-selected': {
                      color: '#FFFFFF',
                    },
                    '&:hover': {
                      color: 'rgba(255,255,255,0.9)',
                      backgroundColor: 'rgba(255,255,255,0.05)',
                    },
                  },
                }}
              >
                <Tab
                  icon={<QueryStatsIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label="Stats"
                />
                <Tab
                  icon={<ViewTimelineIcon sx={{ fontSize: 18 }} />}
                  iconPosition="start"
                  label="Events"
                />
              </Tabs>
            </Box>
          )}

          {/* Stats Panel or Event log — flex:1 so it fills the panel height
              (events locked to full view, no empty space below). */}
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {isCopilot && !showEvents ? (
            // Copilot stats: full-width tile grid (the shared @ae/ui StatsPanel
            // in its isCopilot variant), no StatsContainer (which squeezed it
            // into a narrow column). When caption-sourced tiles are supplied,
            // render Single/Double/Advanced sub-tabs from them instead of teamStats.
            (singleStatTiles || doubleStatTiles || advancedStatTiles) ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 2, pt: 1, flexShrink: 0 }}>
                  {[['single', 'Single'], ['double', 'Double'], ['advanced', 'Advanced']].map(([id, label]) => {
                    const active = statTab === id;
                    return (
                      <Box
                        key={id}
                        component="button"
                        onClick={() => setStatTab(id)}
                        className="no-drag"
                        sx={{
                          background: 'transparent',
                          border: 'none',
                          // A size below the caption tabs and white, not blue, so it
                          // reads as a filter on the stats rather than navigation.
                          borderBottom: active ? '3px solid #fff' : '3px solid transparent',
                          px: 0.5,
                          py: 1,
                          color: active ? '#fff' : 'rgba(255,255,255,0.45)',
                          fontFamily: '"Barlow", sans-serif',
                          fontSize: Math.round(32 * copilotScale),
                          fontWeight: 600,
                          letterSpacing: 1,
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                          '&:hover': { color: '#fff' },
                        }}
                      >
                        {label}
                      </Box>
                    );
                  })}
                  {/* Animate the on-air stats graphic OUT. Only usable, and
                      highlighted, while a stats graphic is on air. */}
                  {onClearStatsGraphic && (
                    <Box sx={{ ml: 'auto' }}>
                      <ClearButton
                        label="Deanimate Clock Stats"
                        onClick={onClearStatsGraphic}
                        live={statsLive}
                        disabled={!statsLive}
                        title={statsLive ? 'Animate out the stats graphic on air' : 'No stats graphic on air'}
                      />
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  {statTab === 'advanced' ? (
                    <CopilotAdvancedStatsGrid
                      stats={advancedStatTiles || []}
                      leftColor={homeAccent}
                      rightColor={awayAccent}
                      activeKey={activeStatCaption}
                      onStatClick={onStatClick}
                      scale={copilotScale}
                    />
                  ) : statTab === 'double' ? (
                    <CopilotDoubleStatsGrid
                      stats={doubleStatTiles || []}
                      leftColor={homeAccent}
                      rightColor={awayAccent}
                      activeKey={activeStatCaption}
                      onStatClick={onStatClick}
                      scale={copilotScale}
                    />
                  ) : (
                    <CopilotSingleStatsGrid
                      stats={mapSingleTilesToItems(singleStatTiles || [])}
                      leftColor={homeAccent}
                      rightColor={awayAccent}
                      activeKey={activeStatCaption}
                      onStatClick={onStatClick}
                      scale={copilotScale}
                    />
                  )}
                </Box>
              </Box>
            ) : (
              <StatsPanel
                isCopilot
                stats={mapStatsToPanelItems(stats)}
                leftColor={homeAccent}
                rightColor={awayAccent}
                onStatClick={onStatClick}
              />
            )
          ) : (
          <StatsContainer
            title=""
            leftColor={homeAccent}
            rightColor={awayAccent}
          >
            {showEvents ? (
              <EventTimeline
                eventLog={eventLog}
                homeTeam={homeTeamInfo}
                awayTeam={awayTeamInfo}
                homeLineup={mappedHomeLineup}
                awayLineup={mappedAwayLineup}
                onEditEvent={handleEditEvent}
                onRemoveEvent={handleRemoveEvent}
                isCopilot={isCopilot}
              />
            ) : (
              <StatsPanel
                stats={mapStatsToPanelItems(stats)}
                leftColor={homeAccent}
                rightColor={awayAccent}
                isCopilot={isCopilot}
              />
            )}
          </StatsContainer>
          )}
          </Box>
        </>
      )}

      {/* TIMER MODAL */}
      <Dialog open={timerModalOpen} onClose={() => setTimerModalOpen(false)}>
        <DialogTitle>Timer Control</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {timerOptions.map((option) => (
              <Button
                key={option.id}
                onClick={() => {
                  onTimerAction?.(option);
                  setTimerModalOpen(false);
                }}
                variant="outlined"
                fullWidth
              >
                {option.label}
              </Button>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>

      {/* CLOCK DIALOGS */}
      <ClockOptionsDialog
        open={clockOptionsDialogOpen}
        onClose={() => setClockOptionsDialogOpen(false)}
        onSelect={handleClockOptionSelect}
        defaultFocusedOptionId={nextClockOptionId}
      />
      <ClockTimeInputDialog
        open={clockTimeInputDialogOpen}
        onClose={() => setClockTimeInputDialogOpen(false)}
        onConfirm={handleClockTimeConfirm}
        minMinutes={clockConstraints.min}
        maxMinutes={clockConstraints.max}
      />
      <OverrideClockConfirmDialog
        open={!!clockOverride}
        periodLabel={clockOverride?.periodLabel}
        onClose={() => setClockOverride(null)}
        onConfirm={doClockOverride}
      />
      <ResetClockConfirmDialog
        open={resetClockConfirmDialogOpen}
        onClose={() => setResetClockConfirmDialogOpen(false)}
        onConfirm={async () => {
          setResetClockConfirmDialogOpen(false);
          onClockOptionSelect?.({ id: 'reset' });
          // Period + plugin trigger now run on confirm (previously fired the
          // moment the operator picked "reset" from the clock menu, before
          // the dialog was even acknowledged). The plugin emits the event
          // deletes + fixture_result zero + fixture status patches.
          if (setPeriod) setPeriod('PRE-GAME');
          await triggerClockEvent('clock_match_reset', { fixtureSession: null, clockStatus: 0 });
          triggerClockAction('RESET');

          try {
            await sendApplyDataState?.({
              scores: { home: 0, away: 0 },
              homeTeam,
              awayTeam,
              redCardTabs: { home: 0, away: 0 },
            });
          } catch (e) { console.error('Failed to reset broker score', e); }

          if (fixtureId) {
            try {
              const session = getStoredSession();
              const patchId = `P${Date.now()}_reset_score`;

              const homePatch = sendPatchesFromDrivePreset({
                collection: 'fixture_result',
                originSite: session?.username || session?.user_name || 'Op1',
                op: 'PATCH',
                valid_from: new Date().toISOString(),
                added_by: session?.username || 'Op1',
                patch_id: patchId + '_h',
                path: 'data.home_team_score',
                value: 0,
                extraKey: { fixture_id: fixtureId, competition_id: competitionId, season_id: seasonId },
              });

              const awayPatch = sendPatchesFromDrivePreset({
                collection: 'fixture_result',
                originSite: session?.username || session?.user_name || 'Op1',
                op: 'PATCH',
                valid_from: new Date().toISOString(),
                added_by: session?.username || 'Op1',
                patch_id: patchId + '_a',
                path: 'data.away_team_score',
                value: 0,
                extraKey: { fixture_id: fixtureId, competition_id: competitionId, season_id: seasonId },
              });

              await Promise.all([
                dds.http.request({ query: homePatch.query, variables: homePatch.variables }),
                dds.http.request({ query: awayPatch.query, variables: awayPatch.variables }),
              ]);

              updateFixtureState({ collection: 'fixture_result', data: { home_team_score: 0, away_team_score: 0 } });

              try {
                const resetPreset = resetFixturePreset({
                  fixture_id: fixtureId,
                  ...(competitionId && { competition_id: competitionId }),
                  ...(seasonId && { season_id: seasonId }),
                  reason: 'operator_reset',
                  deleteBase: true,
                });
                await dds.http.request({ query: resetPreset.query, variables: resetPreset.variables });

                // The backend fixture reset deliberately skips team_stat
                // (BB_DataWorldService/src/constants.js LIVE_COLLECTIONS),
                // so our AeDrive-authoritative *Opus rows survive with stale
                // counts. Zero them for both teams here.
                try {
                  const opusStatTypes = ['totalYellowCardOpus', 'totalRedCardOpus'];
                  const teamIds = [homeTeam?.team_id, awayTeam?.team_id].filter(Boolean);
                  await Promise.all(
                    teamIds.flatMap((teamId) =>
                      opusStatTypes.map((statType) =>
                        upsertTeamStat({
                          fixtureId,
                          competitionId,
                          seasonId,
                          teamId,
                          statType,
                          value: 0,
                        })
                      )
                    )
                  );
                } catch (statErr) {
                  console.error('Failed to zero Opus card stats on fixture reset', statErr);
                }

                if (publishGoalEvent) {
                  publishGoalEvent({
                    is_nle_enabled: false,
                    mq_publish_enabled: true,
                    user_role: userRole,
                    fixtureId,
                    event_key: 'clock_match_reset',
                    event_op: 'create',
                    event_data: {
                      event_key: 'clock_match_reset',
                      event_type: 'ClockMatchReset',
                      event_name: 'Match Reset',
                      event_op: 'create',
                      fixture_id: fixtureId,
                      competition_id: competitionId,
                      season_id: seasonId,
                      game_period: fixture?.fixture_session ?? null,
                      reset_type: 'full',
                      reset_reason: 'operator_reset',
                      triggered_by: session?.username || session?.user_name || null,
                      time_in_seconds: 0,
                      updatedAt: new Date().toISOString(),
                      ...(fixtureName ? { fixture_name: fixtureName } : {}),
                    },
                  });
                }
                onNotify?.('Clock, Score and Match reset successfully', 'success');
              } catch (e) {
                console.error('Failed to reset fixture in DWS', e);
                onNotify?.('Clock & Score reset, but DWS reset failed. Check logs.', 'error');
              }
            } catch (e) {
              console.error('Failed to patch score in DDS', e);
              onNotify?.('Failed to reset score in DDS.', 'error');
            }
          }
        }}
      />

      {isCopilot && !hideClock && (
        <RemoveGoalConfirmDialog
          open={Boolean(removeGoalSide)}
          teamName={removeGoalSide === 'home' ? homeTeamInfo.name : removeGoalSide === 'away' ? awayTeamInfo.name : ''}
          onClose={() => setRemoveGoalSide(null)}
          onConfirm={handleConfirmRemoveGoal}
        />
      )}

      {/* EVENTS modal (copilot) — full event timeline, opened from the tabs header. */}
      {isCopilot && (
        <Dialog
          open={eventsModalOpen}
          onClose={onCloseEventsModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#1a1a1a',
              color: '#fff',
              // Definite height (not minHeight) so the timeline can fill it; the
              // modal opens at a comfortable size instead of hugging one event.
              // Responsive: caps to the viewport on shorter screens.
              height: 'min(620px, 82vh)',
              borderRadius: 2,
              borderTop: '6px solid #feec67',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <Typography sx={{ fontWeight: 700, letterSpacing: 1 }}>EVENTS</Typography>
            <Box
              component="button"
              onClick={onCloseEventsModal}
              aria-label="Close"
              sx={{ border: 'none', background: 'transparent', color: '#fff', cursor: 'pointer', fontSize: 22, lineHeight: 1, px: 0.5 }}
            >
              ×
            </Box>
          </Box>
          {/* Flex column + child flex:1 forces the timeline to fill the modal
              height regardless of its own (max)height — so it extends with the
              modal instead of hugging a single event. */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', p: 1, display: 'flex', flexDirection: 'column', '& > *': { flex: 1, minHeight: 0 } }}>
            <EventTimeline
              eventLog={eventLog}
              homeTeam={homeTeamInfo}
              awayTeam={awayTeamInfo}
              homeLineup={mappedHomeLineup}
              awayLineup={mappedAwayLineup}
              onEditEvent={handleEditEvent}
              onRemoveEvent={handleRemoveEvent}
              isCopilot={isCopilot}
              autoEditEventId={autoEditEventId}
              onAutoEditHandled={onAutoEditHandled}
            />
          </Box>
        </Dialog>
      )}
    </Box>
  );
});

// PropTypes

StatsPanels.propTypes = {
  score: PropTypes.object,
  stats: PropTypes.array,
  otherGames: PropTypes.array,
  homeAccent: PropTypes.string,
  awayAccent: PropTypes.string,
  onTimerAction: PropTypes.func,
  onTimeControlClick: PropTypes.func,
  isCopilot: PropTypes.bool,
  copilotScale: PropTypes.number,
  eventsModalOpen: PropTypes.bool,
  onCloseEventsModal: PropTypes.func,
  autoEditEventId: PropTypes.string,
  onAutoEditHandled: PropTypes.func,
  singleStatTiles: PropTypes.array,
  doubleStatTiles: PropTypes.array,
  advancedStatTiles: PropTypes.array,
  activeStatCaption: PropTypes.string,
  statsGraphicOnAir: PropTypes.bool,
  onClearStatsGraphic: PropTypes.func,
  onStatClick: PropTypes.func,
  onScoreChange: PropTypes.func,
  onAddGoalClick: PropTypes.func,
  onEditGoalClick: PropTypes.func,
  onClockOptionSelect: PropTypes.func,
  onOpenSubstitutionsDialog: PropTypes.func,
  rendererId: PropTypes.string,
  clockName: PropTypes.string,
  statsViewMode: PropTypes.oneOf(['stats', 'events']),
  onStatsViewModeChange: PropTypes.func,
  hideStats: PropTypes.bool,
  hideClock: PropTypes.bool,
  eventLog: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    clockLabel: PropTypes.string,
    side: PropTypes.oneOf(['home', 'away']),
    eventType: PropTypes.string,
    playerLabel: PropTypes.string,
  })),
  fixtureId: PropTypes.string,
  competitionId: PropTypes.string,
  seasonId: PropTypes.string,
  homeTeam: PropTypes.object,
  awayTeam: PropTypes.object,
  fixture: PropTypes.object,
  result: PropTypes.object,
  profileId: PropTypes.string,
  userRole: PropTypes.string,
  eventService: PropTypes.object,
  triggerScoreUpdate: PropTypes.func,
  updateFixtureState: PropTypes.func,
  homeLineup: PropTypes.object,
  awayLineup: PropTypes.object,
  fixtureEvents: PropTypes.array,
  clockProfile: PropTypes.object,
  period: PropTypes.string,
  setPeriod: PropTypes.func,
  triggerClockAction: PropTypes.func,
  sendApplyDataState: PropTypes.func,
  cancelPendingScoreFinalise: PropTypes.func,
  onNotify: PropTypes.func,
  getRendererIdToUse: PropTypes.func,
  publishGoalEvent: PropTypes.func,
  updateRenderer: PropTypes.func,
};

export default StatsPanels;
